import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container) {
    return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => element.offsetParent !== null || element === document.activeElement);
}

// Safari does not focus a button on click, so the opener may be the body, which is no place to return to.
function canReceiveFocus(element) {
    return Boolean(element?.isConnected) && element !== document.body;
}

// Moves focus into the dialog, keeps Tab inside it, closes it on Escape, and returns focus
// to whatever had it before the dialog opened, or to returnFocusRef when that element is gone
// (the cookie bar's Manage button disappears once a choice is saved).
function useDialogFocus(dialogRef, onClose, returnFocusRef) {
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const dialog = dialogRef.current;
        const previouslyFocused = document.activeElement;
        dialog?.querySelector('button')?.focus();

        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab' || !dialog) {
                return;
            }

            const focusable = getFocusableElements(dialog);
            if (focusable.length === 0) {
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            const target = canReceiveFocus(previouslyFocused) ? previouslyFocused : returnFocusRef?.current;
            target?.focus?.();
        };
    }, [dialogRef, returnFocusRef]);
}

export default useDialogFocus;
